import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChefHat, Settings, Home, User, LogOut, Crown, ChevronDown, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const headerRef = useRef<HTMLElement | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement | null>(null);
  const mobileToggleRef = useRef<HTMLButtonElement | null>(null);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showUserMenu && !(event.target as Element).closest('.user-menu')) {
        setShowUserMenu(false);
      }
      // Close mobile menu when clicking outside the header area
      if (mobileOpen && headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setMobileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu, mobileOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    if (mobileOpen) setMobileOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Close mobile menu on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileOpen) setMobileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  // Focus trap for mobile menu and return focus to toggle on close
  useEffect(() => {
    if (!mobileOpen) {
      // return focus to toggle when closing
      mobileToggleRef.current?.focus();
      return;
    }
    // When opening, move focus to first focusable element inside menu
    const focusable = mobileMenuRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable && focusable[0];
    const last = focusable && focusable[focusable.length - 1];
    first?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (!mobileOpen) return;
      if (e.key !== 'Tab') return;
      if (!first || !last) return;
      // Cycle focus within the mobile menu
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        (last as HTMLElement).focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        (first as HTMLElement).focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen]);

  const navigationItems = [
    { path: '/', label: 'Home', requiresAuth: false, icon: Home },
    { path: '/meal-planner', label: 'Meal Planner', requiresAuth: false, icon: ChefHat },
    // Show Upgrade to anonymous users and authenticated users with free tier
    ...((!user || user.subscriptionTier === 'free') ? [{ path: '/subscription', label: 'Upgrade', requiresAuth: false, icon: Crown }] : []),
    { path: '/preferences', label: 'Preferences', requiresAuth: false, icon: Settings },
    { path: '/admin', label: 'Admin', requiresAuth: true, adminOnly: true, icon: Crown }
  ];

  return (
    <header ref={headerRef} className="bg-white shadow-sm border-b border-gray-200">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 bg-white px-3 py-2 rounded shadow">Skip to content</a>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 gap-2">
          {/* Logo and Brand */}
          <div className="flex items-center flex-shrink-0">
            <Link to="/" className="flex items-center space-x-3">
              <img src="/logo.png" alt="Eatonomic Logo" className="h-10 w-10" width={40} height={40} decoding="async" />
              <span className="text-xl sm:text-2xl font-bold text-gray-800 font-serif">Eatonomic</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-3 flex-1 justify-center" aria-label="Primary">
            {navigationItems.map((item) => {
              // Skip navigation items that require auth if user is not authenticated
              if (item.requiresAuth && !user) return null;
              
              // Skip admin-only items if user is not admin
              if (item.adminOnly && !user?.isAdmin) return null;
              
              const IconComponent = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors duration-200 ${
                    isActive(item.path)
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                  title={`Go to ${item.label.toLowerCase()}`}
                >
                  {IconComponent && <IconComponent className="h-5 w-5" />}
                  <span className="sm:whitespace-nowrap">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Authentication Section */}
          <div className="flex items-center space-x-4">
            {user ? (
              <div className="relative user-menu">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 px-2 sm:px-4 py-2 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors duration-200"
                  title="Open user menu and account options"
                >
                  {/* Show subscription tier icon for paid users, generic user icon for free users */}
                  {user.subscriptionTier === 'free' ? (
                    <User className="h-5 w-5" />
                  ) : user.subscriptionTier === 'premium' ? (
                    <Crown className="h-5 w-5 text-yellow-500" />
                  ) : (
                    <Zap className="h-5 w-5 text-purple-500" />
                  )}
                  <span className="font-medium hidden sm:inline">{user.name || user.email}</span>
                  <ChevronDown className="h-4 w-4 hidden sm:inline" />
                </button>

                {/* User Menu Dropdown */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user.name || user.email}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                                             <div className="flex items-center mt-2">
                         {user.subscriptionTier === 'free' ? (
                           <Crown className="h-4 w-4 mr-2 text-gray-400" />
                         ) : user.subscriptionTier === 'premium' ? (
                           <Crown className="h-4 w-4 mr-2 text-yellow-500" />
                         ) : (
                           <Zap className="h-4 w-4 mr-2 text-purple-500" />
                         )}
                         <span className="text-sm font-medium capitalize">
                           {user.subscriptionTier || 'free'} Plan
                         </span>
                       </div>
                    </div>
                    
                    <div className="py-1">
                      {/* Subscription/Upgrade link - shows "Upgrade" for free users, "Subscription" for paid users */}
                                             <Link
                         to="/subscription"
                         onClick={() => setShowUserMenu(false)}
                         className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                         title={user.subscriptionTier === 'free' ? "Upgrade your subscription" : "Manage your subscription"}
                       >
                                                   {user.subscriptionTier === 'free' ? (
                            <Crown className="h-4 w-4 mr-2 text-gray-400" />
                          ) : user.subscriptionTier === 'premium' ? (
                            <Crown className="h-4 w-4 mr-2 text-yellow-500" />
                          ) : (
                            <Zap className="h-4 w-4 mr-2 text-purple-500" />
                          )}
                         {user.subscriptionTier === 'free' ? 'Upgrade' : 'Subscription'}
                       </Link>
                      <Link
                        to="/account-settings"
                        onClick={() => setShowUserMenu(false)}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                        title="Manage your account settings"
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        Account Settings
                      </Link>
                      {user.isAdmin && (
                        <Link
                          to="/admin"
                          onClick={() => setShowUserMenu(false)}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                          title="Access admin dashboard"
                        >
                          <Crown className="h-4 w-4 mr-2 text-yellow-500" />
                          Admin Dashboard
                        </Link>
                      )}
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          handleLogout();
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                        title="Sign out of your account"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                {/* Login Link */}
                <Link
                  to="/auth"
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                >
                  Login
                </Link>

                {/* Register Link */}
                <Link
                  to="/auth?mode=register"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden ml-2">
            <button
              type="button"
              className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              aria-controls="mobile-menu"
              aria-expanded={mobileOpen}
              aria-label={mobileOpen ? 'Close main menu' : 'Open main menu'}
              onClick={() => setMobileOpen((v) => !v)}
              ref={mobileToggleRef}
            >
              {/* Icon toggle: hamburger vs close */}
              {mobileOpen ? (
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation (Hidden by default) */}
      <div
        id="mobile-menu"
        ref={mobileMenuRef}
        className={`md:hidden ${mobileOpen ? 'block animate-slide-down' : 'hidden'}`}
        role="dialog"
        aria-modal={mobileOpen || undefined}
      >
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          {navigationItems.map((item) => {
            if (item.requiresAuth && !user) return null;
            if (item.adminOnly && !user?.isAdmin) return null;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`block px-3 py-2 rounded-md text-base font-medium ${
                  isActive(item.path)
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setMobileOpen(false)}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      </div>
    </header>
  );
};
